import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Edit2, Trash2, TrendingUp, TrendingDown, Filter, Check, X, XCircle, RefreshCw, Download, GitCompare, AlertCircle, CheckCircle2, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Users, FileSpreadsheet, Upload, Zap, CandlestickChart } from "lucide-react";
import { toast } from "sonner";
import { TradeChartModal } from "@/components/TradeChartModal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fetchContractPrices, getAllCachedPrices, getApiSymbol, loadConfig, getConfig } from "@/services/PriceService";
import { formatDateIST, formatDateTimeForInput, getCurrentDateIST } from "@/lib/dateUtils";
import { calculatePnl, getPnlMultiplier } from "@/utils/pnl";

// Extracted modal components
import { CloseTradeModal, RolloverModal, ReconcileModal, HistoricalImportModal, PartialCloseModal } from "@/components/trades";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Auto-sync visual indicators ──────────────────────────────
// Shows a compact badge cluster next to the trade's symbol when the trade
// came from the SpeedBot auto-sync pipeline, plus broker-match dots for
// entry and (if closed) exit sides.
const MATCH_STATUS_META = {
  MATCHED:           { color: "bg-emerald-400",  label: "Matched in broker (within 0.5%)" },
  MISMATCH:          { color: "bg-amber-400",    label: "Price mismatch vs broker (> 0.5%)" },
  MISSING_IN_BROKER: { color: "bg-rose-500",     label: "Not found in broker tradebook" },
  NOT_CHECKED:       { color: "bg-slate-600",    label: "Broker not checked (no creds or disabled)" },
};

function MatchDot({ status, side }) {
  const meta = MATCH_STATUS_META[status] || MATCH_STATUS_META.NOT_CHECKED;
  return (
    <span
      data-testid={`match-dot-${side}-${status || "none"}`}
      title={`${side === "entry" ? "Entry" : "Exit"} · ${meta.label}`}
      className={`inline-block w-1.5 h-1.5 rounded-full ${meta.color}`}
    />
  );
}

function AutoSyncBadges({ trade }) {
  if (trade?.source !== "speedbot_auto") return null;
  const entryStatus = trade.broker_match_status;
  const exitStatus = trade.broker_exit_match_status;
  const showExit = trade.status === "CLOSED" && !!exitStatus;
  return (
    <span
      data-testid={`autosync-badges-${trade.id}`}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30"
      title="Auto-synced from SpeedBot"
    >
      <Zap className="w-3 h-3 text-amber-400" />
      {entryStatus && <MatchDot status={entryStatus} side="entry" />}
      {showExit && <MatchDot status={exitStatus} side="exit" />}
    </span>
  );
}

export const TradesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState(null);
  
  // Restore ALL filters from URL params
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "open");
  const [symbolFilter, setSymbolFilter] = useState(searchParams.get("symbol") || "all");
  const [directionFilter, setDirectionFilter] = useState(searchParams.get("direction") || "all");
  const [accountFilter, setAccountFilter] = useState(searchParams.get("account") || "all");
  const [strategyFilter, setStrategyFilter] = useState(searchParams.get("strategy") || "all");
  const [marketPrices, setMarketPrices] = useState({});
  const [priceLoading, setPriceLoading] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [manualPrice, setManualPrice] = useState("");
  const [chartConfig, setChartConfig] = useState(null);

  const openTradeChart = (trade) => {
    const levels = [];
    if (trade.entry_price > 0) {
      const isShort = (trade.direction || "").toUpperCase() === "SHORT";
      levels.push({ price: trade.entry_price, title: isShort ? "SHORT" : "LONG", color: isShort ? "#f43f5e" : "#10b981", dashed: false });
    }
    if (trade.sl_price > 0) {
      levels.push({ price: trade.sl_price, title: `SL${trade.sl_order_type ? " · " + trade.sl_order_type : ""}`, color: "#ef4444", dashed: true });
    }
    if (trade.target > 0) {
      levels.push({ price: trade.target, title: "TARGET", color: "#22c55e", dashed: true });
    }
    setChartConfig({
      symbol: trade.symbol,
      exchange: trade.exchange || "NSE",
      expiry: trade.contract_expiry || null,
      refPrice: trade.entry_price || trade.sl_price || null,
      levels,
      subtitle: `${(trade.direction || "").toUpperCase()} · Entry ₹${trade.entry_price?.toLocaleString("en-IN") || "—"}${trade.contract_expiry ? ` · ${trade.contract_expiry}` : ""}`,
    });
  };
  const [priceConfigLoaded, setPriceConfigLoaded] = useState(false);
  const navigate = useNavigate();

  // Sorting state - separate for open and closed trades
  const [openSortConfig, setOpenSortConfig] = useState({ key: 'symbol', direction: 'asc' });
  const [closedSortConfig, setClosedSortConfig] = useState({ key: 'exit_date', direction: 'desc' });
  
  // Pagination state - restore from URL params
  const [openTradesPage, setOpenTradesPage] = useState(parseInt(searchParams.get("openPage")) || 1);
  const [closedTradesPage, setClosedTradesPage] = useState(parseInt(searchParams.get("closedPage")) || 1);
  const [tradesPerPage, setTradesPerPage] = useState(parseInt(searchParams.get("perPage")) || 20);

  // Reset pagination when filters change (but not on initial load from URL)
  const [initialLoad, setInitialLoad] = useState(true);
  const [inactiveBots, setInactiveBots] = useState([]);
  const [autoEnabledBots, setAutoEnabledBots] = useState([]);
  const [marketStatus, setMarketStatus] = useState(null);
  const [refreshingSL, setRefreshingSL] = useState(false);
  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }
    setOpenTradesPage(1);
    setClosedTradesPage(1);
  }, [symbolFilter, directionFilter, accountFilter, strategyFilter]);

  // Close Trade Modal State
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [tradeToClose, setTradeToClose] = useState(null);
  const [closeForm, setCloseForm] = useState({
    exit_price: "",
    exit_date: "",
    exit_comments: ""
  });
  const [closingTrade, setClosingTrade] = useState(false);

  // Partial Close Modal State
  const [showPartialCloseModal, setShowPartialCloseModal] = useState(false);
  const [tradeToPartialClose, setTradeToPartialClose] = useState(null);

  // Rollover Modal State
  const [showRolloverModal, setShowRolloverModal] = useState(false);
  const [tradeToRollover, setTradeToRollover] = useState(null);
  const [rolloverForm, setRolloverForm] = useState({
    exit_price: "",
    exit_date: "",
    exit_comments: "",
    new_entry_price: "",
    new_entry_date: "",
    new_entry_comments: "",
    new_contract_expiry: "",
    new_num_lots: "",
    new_lot_size: ""
  });
  const [rollingOver, setRollingOver] = useState(false);
  const [nextExpiryInfo, setNextExpiryInfo] = useState(null);
  const [loadingNextExpiry, setLoadingNextExpiry] = useState(false);
  const [accounts, setAccounts] = useState([]);

  // Import from Broker State
  const [showImportModal, setShowImportModal] = useState(false);
  const [brokerPositions, setBrokerPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [importingPositions, setImportingPositions] = useState(false);
  const [importAccountId, setImportAccountId] = useState(""); // Account for imported trades
  const [importMode, setImportMode] = useState("positions"); // "positions" or "tradebook"
  
  // Trade Book Import State (for correlated trades)
  const [tradeBookTrades, setTradeBookTrades] = useState([]);
  const [loadingTradeBook, setLoadingTradeBook] = useState(false);
  const [selectedTradeBookTrades, setSelectedTradeBookTrades] = useState([]);
  const [orphanExits, setOrphanExits] = useState([]); // Exits for existing journal trades
  const [selectedOrphanExits, setSelectedOrphanExits] = useState([]);
  const [closingOrphanExits, setClosingOrphanExits] = useState(false);
  const [tradeBookComments, setTradeBookComments] = useState({}); // Comments keyed by trade index

  // Historical Import State
  const [showHistoricalImportModal, setShowHistoricalImportModal] = useState(false);
  const [historicalImportAccountId, setHistoricalImportAccountId] = useState("");
  const [historicalImportFile, setHistoricalImportFile] = useState(null);
  const [importingHistorical, setImportingHistorical] = useState(false);

  // Import Batches Management State
  const [showImportBatchesModal, setShowImportBatchesModal] = useState(false);
  const [importBatches, setImportBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [rollingBackBatch, setRollingBackBatch] = useState(null);

  // Import Log Detail State
  const [selectedImportLog, setSelectedImportLog] = useState(null);
  const [loadingImportLog, setLoadingImportLog] = useState(false);
  const [showImportLogDetail, setShowImportLogDetail] = useState(false);

  // Reconcile Modal State
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  const [loadingReconcile, setLoadingReconcile] = useState(false);
  const [reconcileExitPrice, setReconcileExitPrice] = useState("");
  const [reconcileExitDate, setReconcileExitDate] = useState("");
  const [reconcileExitComments, setReconcileExitComments] = useState("");
  const [closingFromReconcile, setClosingFromReconcile] = useState(false);
  const [strategies, setStrategies] = useState([]);

  useEffect(() => {
    // Load price config from backend on mount
    const initConfig = async () => {
      await loadConfig();
      setPriceConfigLoaded(true);
    };
    initConfig();
    fetchTrades();
    fetchAccounts();
    fetchStrategies();
  }, []);

  // Re-fetch trades when returning to this page (URL changes)
  useEffect(() => {
    // This will trigger when navigating back to this page
    fetchTrades();
  }, [location.pathname]);

  // Persist account filter and tab in URL params
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (accountFilter && accountFilter !== "all") {
      params.set("account", accountFilter);
    } else {
      params.delete("account");
    }
    if (activeTab && activeTab !== "open") {
      params.set("tab", activeTab);
    } else {
      params.delete("tab");
    }
    setSearchParams(params, { replace: true });
  }, [accountFilter, activeTab, setSearchParams]);

  // Handle "Close Trade" action from notification bell
  useEffect(() => {
    const action = searchParams.get("action");
    const symbolParam = searchParams.get("symbol");
    const accountIdParam = searchParams.get("account_id");
    
    if (action === "close" && symbolParam && trades.length > 0) {
      // Find the matching open trade
      const matchingTrade = trades.find(t => 
        t.status === "OPEN" && 
        t.symbol.toUpperCase() === symbolParam.toUpperCase() &&
        (!accountIdParam || t.account_id === accountIdParam)
      );
      
      if (matchingTrade) {
        // Open the close trade modal
        openCloseTradeModal(matchingTrade);
        
        // Clear the action params from URL
        const params = new URLSearchParams(searchParams);
        params.delete("action");
        params.delete("symbol");
        params.delete("account_id");
        params.delete("signal_id");
        setSearchParams(params, { replace: true });
      } else {
        toast.error(`No open trade found for ${symbolParam}`);
        // Clear params anyway
        const params = new URLSearchParams(searchParams);
        params.delete("action");
        params.delete("symbol");
        params.delete("account_id");
        params.delete("signal_id");
        setSearchParams(params, { replace: true });
      }
    }
  }, [trades, searchParams, setSearchParams]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get("/accounts");
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const response = await api.get("/strategies");
      setStrategies(response.data);
    } catch (error) {
      console.error("Error fetching strategies:", error);
    }
  };

  // Helper to get account name by ID
  const getAccountName = (accountId) => {
    if (!accountId) return null;
    const account = accounts.find(a => a.id === accountId);
    return account?.name || accountId;
  };

  const fetchTrades = async () => {
    try {
      const response = await api.get("/trades");
      setTrades(response.data);
    } catch (error) {
      console.error("Error fetching trades:", error);
      toast.error("Failed to load trades");
    } finally {
      setLoading(false);
    }
  };

  // Use the centralized price service for faster caching
  const fetchMarketPrices = useCallback(async (forceRefresh = false) => {
    const openTradesData = trades.filter(t => t.status === "OPEN");
    if (openTradesData.length === 0) return;

    // First, immediately show cached prices if available
    const cachedPrices = getAllCachedPrices();
    if (Object.keys(cachedPrices).length > 0 && !forceRefresh) {
      setMarketPrices(prev => ({ ...prev, ...cachedPrices }));
    }

    setPriceLoading(true);
    try {
      // Build contracts list for price service
      const contracts = openTradesData.map(trade => ({
        symbol: trade.symbol,
        exchange: trade.exchange,
        contract_expiry: trade.contract_expiry || null
      }));

      // Fetch prices using centralized service (handles caching)
      const prices = await fetchContractPrices(contracts, forceRefresh);
      setMarketPrices(prices);
    } catch (error) {
      console.error("Error fetching market prices:", error);
    } finally {
      setPriceLoading(false);
    }
  }, [trades]);

  useEffect(() => {
    if (trades.length > 0 && activeTab === "open" && priceConfigLoaded) {
      fetchMarketPrices(false); // Use cache on initial load
      
      // Use configurable refresh interval from backend settings
      const config = getConfig();
      const refreshInterval = config.REFRESH_INTERVAL_MS || 15000;
      console.log(`Price refresh interval: ${refreshInterval}ms`);
      
      const interval = setInterval(() => fetchMarketPrices(true), refreshInterval);
      return () => clearInterval(interval);
    }
  }, [trades, activeTab, fetchMarketPrices, priceConfigLoaded]);

  // Poll inactive SpeedBot bots while the Open Trades tab is active so the
  // banner reflects the latest sync snapshot (refreshed every 60s).
  useEffect(() => {
    if (activeTab !== "open") return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/speedbot-api/inactive-bots");
        if (!cancelled) setInactiveBots(res.data?.inactive_bots || []);
      } catch (err) {
        // Non-fatal — banner just won't appear this tick.
        console.debug("inactive-bots poll failed:", err?.message || err);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [activeTab]);

  // Poll recently auto-enabled bot mappings (24h window) so the user can
  // review/disable any forward-test bots that started flowing to the journal.
  useEffect(() => {
    if (activeTab !== "open") return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/speedbot-api/recent-auto-enabled-bots?hours=24");
        if (!cancelled) setAutoEnabledBots(res.data?.bots || []);
      } catch (err) {
        console.debug("auto-enabled-bots poll failed:", err?.message || err);
      }
    };
    load();
    const t = setInterval(load, 120000); // every 2 min — slower than inactive-bots
    return () => { cancelled = true; clearInterval(t); };
  }, [activeTab]);

  const dismissAutoEnabledBot = async (accountId, botId) => {
    try {
      await api.post(`/speedbot-api/bot-mappings/${accountId}/${botId}/disable`);
      setAutoEnabledBots((prev) => prev.filter(
        (b) => !(b.account_id === accountId && b.bot_id === botId)
      ));
      toast.success("Bot disabled — will no longer sync to your journal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to disable bot");
    }
  };

  // NSE / MCX market-status pill — refreshed every 60s. Renders for both
  // tabs so the user always knows whether the system is actively polling.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/speedbot-api/market-status");
        if (!cancelled) setMarketStatus(res.data || null);
      } catch (err) {
        console.debug("market-status poll failed:", err?.message || err);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // ── SL / SAR status helper ─────────────────────────────────────────────
  // Returns one of: "missed" (CMP has crossed SL, trade is not closed),
  // "near" (CMP within 0.5% of SL), "safe" (SL set, CMP comfortably away),
  // or null (no SL on file).
  //
  // Stale-SL guard: an SL whose `sl_checked_at` predates the trade's
  // `entry_date` belongs to a previous position on the same symbol (e.g.,
  // user closed at target and re-entered). Treat it as "no SL on file"
  // until the next signals fetch refreshes it — otherwise we'd flash a
  // false MISSED on a brand-new trade.
  const SL_NEAR_THRESHOLD_PCT = 0.5;
  const evaluateSlStatus = (trade, cmp) => {
    const sl = trade.sl_price;
    if (!sl || sl <= 0 || cmp == null) return null;
    if (trade.sl_checked_at && trade.entry_date) {
      const checkedAt = new Date(trade.sl_checked_at).getTime();
      const entryAt   = new Date(trade.entry_date).getTime();
      if (Number.isFinite(checkedAt) && Number.isFinite(entryAt) && checkedAt < entryAt) {
        return null; // stale — belongs to a prior closed position
      }
    }
    const pctAway = Math.abs(cmp - sl) / sl * 100;
    if (trade.direction === "LONG") {
      if (cmp <= sl) return "missed";
    } else if (trade.direction === "SHORT") {
      if (cmp >= sl) return "missed";
    }
    if (pctAway <= SL_NEAR_THRESHOLD_PCT) return "near";
    return "safe";
  };

  const calculateUnrealizedPnL = (trade) => {
    const key = `${trade.exchange}-${trade.symbol}`;
    const currentPrice = marketPrices[key];
    
    if (!currentPrice) return null;
    
    // Get remaining lots (for trades with partial closes)
    const remainingLots = trade.remaining_lots ?? trade.num_lots;
    const partialPnl = trade.partial_pnl_amount || 0;
    
    // Calculate unrealized P&L on remaining lots only
    let pnlPoints;
    if (trade.direction === "LONG") {
      pnlPoints = currentPrice - trade.entry_price;
    } else {
      pnlPoints = trade.entry_price - currentPrice;
    }
    
    // Unrealized = P&L on remaining lots using correct multiplier
    const multiplier = getPnlMultiplier(trade);
    const unrealizedOnRemaining = pnlPoints * remainingLots * multiplier;
    
    // Total P&L = Realized (from partial closes) + Unrealized (on remaining)
    const totalPnlAmount = partialPnl + unrealizedOnRemaining;
    
    return { 
      pnlPoints, 
      pnlAmount: totalPnlAmount, 
      currentPrice,
      unrealizedOnRemaining,
      realizedFromPartials: partialPnl,
      remainingLots
    };
  };

  const handleManualPriceUpdate = (trade) => {
    setEditingPrice(trade.id);
    const key = `${trade.exchange}-${trade.symbol}`;
    setManualPrice(marketPrices[key] || "");
  };

  const saveManualPrice = (trade) => {
    if (manualPrice && !isNaN(manualPrice)) {
      const key = `${trade.exchange}-${trade.symbol}`;
      setMarketPrices(prev => ({
        ...prev,
        [key]: parseFloat(manualPrice)
      }));
      toast.success("Current price updated");
    }
    setEditingPrice(null);
    setManualPrice("");
  };

  const cancelManualPrice = () => {
    setEditingPrice(null);
    setManualPrice("");
  };

  const handleDelete = async () => {
    if (!tradeToDelete) return;
    
    try {
      await api.delete(`/trades/${tradeToDelete}`);
      toast.success("Trade deleted successfully");
      fetchTrades();
    } catch (error) {
      console.error("Error deleting trade:", error);
      toast.error("Failed to delete trade");
    } finally {
      setDeleteDialogOpen(false);
      setTradeToDelete(null);
    }
  };

  // Open Close Trade Modal
  const openCloseTradeModal = (trade) => {
    const key = `${trade.exchange}-${trade.symbol}`;
    const currentPrice = marketPrices[key];
    
    setTradeToClose(trade);
    setCloseForm({
      exit_price: currentPrice || "",
      exit_date: formatDateTimeForInput(),
      exit_comments: ""
    });
    setShowCloseModal(true);
  };

  // Open Partial Close Modal
  const openPartialCloseModal = (trade) => {
    setTradeToPartialClose(trade);
    setShowPartialCloseModal(true);
  };

  // Handle Partial Close Success
  const handlePartialCloseSuccess = (updatedTrade) => {
    // Refresh trades list
    fetchTrades();
  };

  // Handle Close Trade
  const handleCloseTrade = async () => {
    if (!tradeToClose || !closeForm.exit_price || !closeForm.exit_date) {
      toast.error("Please fill in exit price and date");
      return;
    }

    setClosingTrade(true);
    try {
      const exitPrice = parseFloat(closeForm.exit_price);
      const hasPartialCloses = tradeToClose.exit_legs && tradeToClose.exit_legs.length > 0;
      const remainingLots = tradeToClose.remaining_lots ?? tradeToClose.num_lots;

      if (hasPartialCloses && remainingLots > 0) {
        // Route through partial-close endpoint to maintain correct P&L accounting
        const response = await api.post(`/trades/${tradeToClose.id}/partial-close`, {
          exit_date: closeForm.exit_date,
          exit_price: exitPrice,
          exit_lots: remainingLots,
          exit_type: "Final",
          notes: closeForm.exit_comments || "Final close of remaining lots",
        });
        const pnlAmount = response.data.trade?.pnl_amount || 0;
        toast.success(`Trade fully closed. Total P&L: ₹${pnlAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`);
      } else {
        // Standard close (no partial closes)
        const { pnlPoints, pnlAmount } = calculatePnl({
          direction: tradeToClose.direction,
          entryPrice: tradeToClose.entry_price,
          exitPrice,
          numLots: tradeToClose.num_lots,
          trade: tradeToClose,
        });

        await api.put(`/trades/${tradeToClose.id}`, {
          exit_price: exitPrice,
          exit_date: closeForm.exit_date,
          exit_comments: closeForm.exit_comments,
          pnl_points: pnlPoints,
          pnl_amount: pnlAmount,
          status: "CLOSED"
        });

        toast.success(`Trade closed with P&L: ₹${pnlAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`);
      }

      setShowCloseModal(false);
      setTradeToClose(null);
      fetchTrades();
    } catch (error) {
      console.error("Error closing trade:", error);
      toast.error("Failed to close trade: " + (error.response?.data?.detail || error.message));
    } finally {
      setClosingTrade(false);
    }
  };

  // Fetch next expiry price for rollover
  const fetchNextExpiryPrice = async (trade) => {
    setLoadingNextExpiry(true);
    setNextExpiryInfo(null);
    try {
      const apiSymbol = getApiSymbol(trade.symbol);
      const mcxSymbols = ["GOLD", "GOLDM", "GOLDPETAL", "SILVER", "SILVERM", "SILVERMIC", 
                          "COPPER", "CRUDEOIL", "CRUDEOILM", "NATURALGAS", "ZINC", "ALUMINIUM"];
      const isMCX = mcxSymbols.some(mcx => apiSymbol.toUpperCase().includes(mcx));
      const exchange = trade.exchange === "MCX" || isMCX ? "MCX" : "NSE";
      
      const response = await api.post("/next-expiry-price", {
        symbol: apiSymbol,
        exchange
      });
      
      if (response.data.status && response.data.data?.ltp) {
        setNextExpiryInfo(response.data.data);
        return response.data.data.ltp;
      } else {
        toast.info(response.data.message || "Could not fetch next expiry price");
        return null;
      }
    } catch (error) {
      console.error("Error fetching next expiry price:", error);
      toast.error("Failed to fetch next expiry price");
      return null;
    } finally {
      setLoadingNextExpiry(false);
    }
  };

  // Open Rollover Modal
  const openRolloverModal = async (trade) => {
    const key = `${trade.exchange}-${trade.symbol}`;
    const currentPrice = marketPrices[key];
    
    setTradeToRollover(trade);
    setShowRolloverModal(true);
    
    // Set initial form with current prices and trade data
    setRolloverForm({
      exit_price: currentPrice || "",
      exit_date: formatDateTimeForInput(),
      exit_comments: "",
      new_entry_price: "",  // Will be populated after fetching next expiry
      new_entry_date: formatDateTimeForInput(),
      new_entry_comments: "",
      new_contract_expiry: "",  // Will be populated after fetching next expiry
      new_num_lots: trade.num_lots || "",
      new_lot_size: trade.lot_size || ""
    });
    
    // Fetch next expiry price asynchronously
    const nextExpiryPrice = await fetchNextExpiryPrice(trade);
    if (nextExpiryPrice) {
      setRolloverForm(prev => ({
        ...prev,
        new_entry_price: nextExpiryPrice,
        new_contract_expiry: nextExpiryInfo?.next_expiry || ""
      }));
    }
  };

  // Handle Rollover
  const handleRollover = async () => {
    if (!tradeToRollover || !rolloverForm.exit_price || !rolloverForm.new_entry_price) {
      toast.error("Please fill in exit and new entry prices");
      return;
    }

    setRollingOver(true);
    try {
      const exitPrice = parseFloat(rolloverForm.exit_price);
      const newEntryPrice = parseFloat(rolloverForm.new_entry_price);
      const newNumLots = parseInt(rolloverForm.new_num_lots) || tradeToRollover.num_lots;
      const newLotSize = parseInt(rolloverForm.new_lot_size) || tradeToRollover.lot_size;
      
      // Calculate P&L for closing trade (using original trade's lots/lot_size)
      // Calculate P&L for closing leg using correct multiplier
      const { pnlPoints, pnlAmount } = calculatePnl({
        direction: tradeToRollover.direction,
        entryPrice: tradeToRollover.entry_price,
        exitPrice,
        numLots: tradeToRollover.num_lots,
        trade: tradeToRollover,
      });

      // 1. Close the existing trade - only send required fields
      const closePayload = {
        exit_price: exitPrice,
        exit_date: rolloverForm.exit_date,
        exit_comments: rolloverForm.exit_comments,
        pnl_points: pnlPoints,
        pnl_amount: pnlAmount,
        status: "CLOSED"
      };
      
      console.log("Closing trade with payload:", closePayload);
      await api.put(`/trades/${tradeToRollover.id}`, closePayload);

      // 2. Create new trade in next period with updated lots/expiry
      const newTrade = {
        symbol: tradeToRollover.symbol,
        exchange: tradeToRollover.exchange,
        direction: tradeToRollover.direction,
        entry_price: newEntryPrice,
        entry_date: rolloverForm.new_entry_date,
        num_lots: newNumLots,
        lot_size: newLotSize,
        contract_expiry: rolloverForm.new_contract_expiry || null,
        account_id: tradeToRollover.account_id,
        strategy_id: tradeToRollover.strategy_id,
        entry_comments: rolloverForm.new_entry_comments || `Rolled over from previous position`,
        monthly_trend: tradeToRollover.monthly_trend,
        stop_loss: tradeToRollover.stop_loss,
        target: tradeToRollover.target
      };

      console.log("Creating new trade with payload:", newTrade);
      await api.post("/trades", newTrade);

      toast.success(`Rollover complete! Closed at ₹${exitPrice}, New position at ₹${newEntryPrice}`);
      setShowRolloverModal(false);
      setTradeToRollover(null);
      fetchTrades();
    } catch (error) {
      console.error("Error during rollover:", error);
      toast.error("Failed to rollover: " + (error.response?.data?.detail || error.message));
    } finally {
      setRollingOver(false);
    }
  };

  // ============================================================
  // IMPORT FROM BROKER FUNCTIONS
  // ============================================================

  const fetchBrokerPositions = async (accountId) => {
    if (!accountId || accountId === "none") {
      toast.error("Please select an account first");
      return;
    }
    
    setLoadingPositions(true);
    setBrokerPositions([]);
    try {
      const response = await api.get(`/broker-positions?account_id=${accountId}`);
      if (response.data.status) {
        setBrokerPositions(response.data.data || []);
        setSelectedPositions([]); // Reset selection
        if (response.data.data?.length === 0) {
          toast.info("No active positions found at broker");
        }
      } else {
        toast.error(response.data.message || "Failed to fetch positions");
        setBrokerPositions([]);
      }
    } catch (error) {
      console.error("Error fetching broker positions:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.detail || error.message;
      toast.error("Failed to fetch broker positions: " + errorMsg);
      setBrokerPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  };

  const openImportModal = async () => {
    setShowImportModal(true);
    setImportAccountId(""); // Reset account selection
    setBrokerPositions([]); // Clear previous positions
    setSelectedPositions([]);
    setTradeBookTrades([]); // Clear trade book
    setSelectedTradeBookTrades([]);
    setOrphanExits([]);
    setSelectedOrphanExits([]);
    setImportMode("positions"); // Default to positions
    // Don't auto-fetch - wait for user to select account
  };
  
  const handleAccountSelectForImport = async (accountId) => {
    setImportAccountId(accountId);
    if (accountId && accountId !== "none") {
      // Fetch based on current mode
      if (importMode === "positions") {
        await fetchBrokerPositions(accountId);
      } else {
        await fetchTradeBook(accountId);
      }
    } else {
      setBrokerPositions([]);
      setSelectedPositions([]);
      setTradeBookTrades([]);
      setSelectedTradeBookTrades([]);
      setOrphanExits([]);
      setSelectedOrphanExits([]);
    }
  };

  const togglePositionSelection = (position) => {
    // Don't allow selection of already imported positions
    if (position.already_in_journal) {
      toast.info(`${position.base_symbol} is already in your journal`);
      return;
    }
    
    const key = position.tradingsymbol;
    if (selectedPositions.includes(key)) {
      setSelectedPositions(selectedPositions.filter(k => k !== key));
    } else {
      setSelectedPositions([...selectedPositions, key]);
    }
  };

  const selectAllPositions = () => {
    // Only select positions that are not already imported
    const selectablePositions = brokerPositions.filter(p => !p.already_in_journal);
    
    if (selectedPositions.length === selectablePositions.length) {
      setSelectedPositions([]);
    } else {
      setSelectedPositions(selectablePositions.map(p => p.tradingsymbol));
    }
  };

  const handleImportPositions = async () => {
    if (!importAccountId || importAccountId === "none") {
      toast.error("Please select an account");
      return;
    }
    
    // Filter out any already imported positions
    const validSelections = selectedPositions.filter(key => {
      const pos = brokerPositions.find(p => p.tradingsymbol === key);
      return pos && !pos.already_in_journal;
    });

    if (validSelections.length === 0) {
      toast.error("Please select at least one new position to import");
      return;
    }

    setImportingPositions(true);
    try {
      const positionsToImport = brokerPositions
        .filter(p => validSelections.includes(p.tradingsymbol))
        .map(p => ({
          tradingsymbol: p.tradingsymbol,
          exchange: p.exchange,
          direction: p.direction,
          netqty: p.netqty,
          avgnetprice: p.avgnetprice,
          ltp: p.ltp,
          account_id: importAccountId,
          strategy_id: null,
          lotsize: p.lotsize || p.suggested_lot_size,
          expirydate: p.expirydate,
          entry_date: p.entry_date || null  // Entry date from trade book if available
        }));

      const response = await api.post("/import-broker-positions", positionsToImport);
      
      if (response.data.status) {
        const skippedCount = response.data.skipped_duplicates?.length || 0;
        const importedCount = response.data.imported_count || 0;
        if (skippedCount > 0) {
          toast.success(`Successfully imported ${importedCount} trades. ${skippedCount} already existed (skipped)`);
        } else {
          toast.success(`Successfully imported ${importedCount} trades`);
        }
        setShowImportModal(false);
        setBrokerPositions([]);
        setSelectedPositions([]);
        setImportAccountId("");
        fetchTrades();
      } else {
        toast.error("Failed to import positions");
      }
    } catch (error) {
      console.error("Error importing positions:", error);
      toast.error("Failed to import: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportingPositions(false);
    }
  };

  // ============================================================
  // TRADE BOOK IMPORT FUNCTIONS (for multiple trades per symbol)
  // ============================================================

  const fetchTradeBook = async (accountId) => {
    if (!accountId || accountId === "none") return;
    
    setLoadingTradeBook(true);
    setTradeBookTrades([]);
    setSelectedTradeBookTrades([]);
    setOrphanExits([]);
    setSelectedOrphanExits([]);
    setTradeBookComments({}); // Reset comments
    
    try {
      const response = await api.get(`/broker-trade-book?account_id=${accountId}`);
      
      if (response.data.status) {
        setTradeBookTrades(response.data.data || []);
        // trades_to_close contains trades that can close existing OPEN journal trades
        setOrphanExits(response.data.trades_to_close || []);
        
        const totalTrades = (response.data.data?.length || 0);
        const canClose = response.data.can_close_count || 0;
        const canImport = response.data.can_import_count || 0;
        
        if (totalTrades === 0) {
          toast.info("No trades found in today's trade book");
        } else {
          toast.success(response.data.message || `Found ${totalTrades} trades`);
        }
      } else {
        toast.error(response.data.message || "Failed to fetch trade book");
      }
    } catch (error) {
      console.error("Error fetching trade book:", error);
      toast.error("Failed to fetch trade book: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingTradeBook(false);
    }
  };

  const toggleOrphanExitSelection = (orphanExit, index) => {
    const key = `orphan_${orphanExit.journal_trade_id}_${index}`;
    if (selectedOrphanExits.includes(key)) {
      setSelectedOrphanExits(selectedOrphanExits.filter(k => k !== key));
    } else {
      setSelectedOrphanExits([...selectedOrphanExits, key]);
    }
  };

  const selectAllOrphanExits = () => {
    if (selectedOrphanExits.length === orphanExits.length) {
      setSelectedOrphanExits([]);
    } else {
      setSelectedOrphanExits(orphanExits.map((o, idx) => `orphan_${o.journal_trade_id}_${idx}`));
    }
  };

  const handleCloseOrphanExits = async () => {
    if (selectedOrphanExits.length === 0) {
      toast.error("Please select at least one trade to close");
      return;
    }

    setClosingOrphanExits(true);
    try {
      const exitsToClose = selectedOrphanExits.map(key => {
        const parts = key.split("_");
        const idx = parseInt(parts[parts.length - 1]);
        const orphan = orphanExits[idx];
        return {
          journal_trade_id: orphan.journal_trade_id,
          exit_price: orphan.exit_price,
          exit_time: orphan.exit_time,
          exit_order_id: orphan.exit_order_id
        };
      });

      const response = await api.post("/close-trades-from-orphan-exits", exitsToClose);
      
      if (response.data.status) {
        toast.success(`Successfully closed ${response.data.closed_count} trade(s)`);
        setShowImportModal(false);
        setOrphanExits([]);
        setSelectedOrphanExits([]);
        fetchTrades(); // Refresh the trades list
      } else {
        toast.error("Failed to close trades");
      }
    } catch (error) {
      console.error("Error closing trades:", error);
      toast.error("Failed to close: " + (error.response?.data?.detail || error.message));
    } finally {
      setClosingOrphanExits(false);
    }
  };

  const toggleTradeBookSelection = (trade, index) => {
    // Only allow selection of actionable trades
    const isSelectable = trade.action === "import_new" || trade.action === "close_existing";
    if (!isSelectable) {
      if (trade.already_in_journal) {
        toast.info(`This trade is already fully imported`);
      }
      return;
    }
    
    const key = `${trade.tradingsymbol}_${trade.entry_time}_${index}`;
    if (selectedTradeBookTrades.includes(key)) {
      setSelectedTradeBookTrades(selectedTradeBookTrades.filter(k => k !== key));
    } else {
      setSelectedTradeBookTrades([...selectedTradeBookTrades, key]);
    }
  };

  const selectAllTradeBookTrades = () => {
    const selectableTrades = tradeBookTrades
      .map((t, idx) => ({ ...t, _idx: idx }))
      .filter(t => t.action === "import_new" || t.action === "close_existing");
    
    if (selectedTradeBookTrades.length === selectableTrades.length) {
      setSelectedTradeBookTrades([]);
    } else {
      setSelectedTradeBookTrades(selectableTrades.map(t => `${t.tradingsymbol}_${t.entry_time}_${t._idx}`));
    }
  };

  const handleImportFromTradeBook = async () => {
    if (!importAccountId || importAccountId === "none") {
      toast.error("Please select an account");
      return;
    }
    
    // Separate trades by action type
    const tradesToImport = [];
    const tradesToClose = [];
    
    selectedTradeBookTrades.forEach(key => {
      const parts = key.split("_");
      const idx = parseInt(parts[parts.length - 1]);
      const trade = tradeBookTrades[idx];
      
      if (!trade) return;
      
      if (trade.action === "import_new") {
        tradesToImport.push({
          tradingsymbol: trade.tradingsymbol,
          exchange: trade.exchange || "NFO",
          direction: trade.direction,
          entry_price: trade.entry_price,
          entry_time: trade.entry_time,
          entry_quantity: trade.entry_quantity,
          exit_price: trade.exit_price,
          exit_time: trade.exit_time,
          exit_quantity: trade.exit_quantity,
          status: trade.status,
          account_id: importAccountId,
          strategy_id: null,
          lotsize: trade.lotsize,
          expirydate: trade.expirydate
        });
      } else if (trade.action === "close_existing" && trade.matching_journal_trade) {
        tradesToClose.push({
          journal_trade_id: trade.matching_journal_trade.id,
          exit_price: trade.exit_price,
          exit_time: trade.exit_time,
          exit_order_id: trade.exit_order_id,
          exit_comments: tradeBookComments[idx] || "" // Include user comments
        });
      }
    });

    if (tradesToImport.length === 0 && tradesToClose.length === 0) {
      toast.error("Please select at least one trade to import or close");
      return;
    }

    setImportingPositions(true);
    try {
      let importedCount = 0;
      let closedCount = 0;
      let skippedCount = 0;
      
      // Import new trades
      if (tradesToImport.length > 0) {
        const importResponse = await api.post("/import-from-trade-book", tradesToImport);
        if (importResponse.data.status) {
          importedCount = importResponse.data.imported_count || 0;
          skippedCount = importResponse.data.skipped_duplicates?.length || 0;
        }
      }
      
      // Close existing trades
      if (tradesToClose.length > 0) {
        const closeResponse = await api.post("/close-trades-from-orphan-exits", tradesToClose);
        if (closeResponse.data.status) {
          closedCount = closeResponse.data.closed_count || 0;
        }
      }
      
      // Show success message
      let msg = "";
      if (importedCount > 0) msg += `Imported ${importedCount} new trade(s)`;
      if (skippedCount > 0) msg += (msg ? ", " : "") + `${skippedCount} already existed (skipped)`;
      if (closedCount > 0) msg += (msg ? ", " : "") + `Closed ${closedCount} existing trade(s)`;
      
      if (msg) {
        toast.success(msg);
        setShowImportModal(false);
        setTradeBookTrades([]);
        setSelectedTradeBookTrades([]);
        setImportAccountId("");
        fetchTrades();
      } else {
        toast.error("No trades were processed");
      }
    } catch (error) {
      console.error("Error processing trade book:", error);
      toast.error("Failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportingPositions(false);
    }
  };

  const handleImportModeChange = (mode) => {
    setImportMode(mode);
    // Clear selections when switching modes
    setSelectedPositions([]);
    setSelectedTradeBookTrades([]);
    
    // Fetch data for the new mode if account is selected
    if (importAccountId && importAccountId !== "none") {
      if (mode === "positions") {
        fetchBrokerPositions(importAccountId);
      } else {
        fetchTradeBook(importAccountId);
      }
    }
  };

  // ============================================================
  // HISTORICAL IMPORT FUNCTIONS
  // ============================================================

  const [lastImportBatchId, setLastImportBatchId] = useState(null);

  const handleHistoricalImport = async () => {
    if (!historicalImportAccountId || historicalImportAccountId === "none") {
      toast.error("Please select an account");
      return;
    }
    
    if (!historicalImportFile) {
      toast.error("Please select an Excel file");
      return;
    }
    
    setImportingHistorical(true);
    try {
      const formData = new FormData();
      formData.append("file", historicalImportFile);
      
      const response = await api.post(
        `/import-historical-trades/${historicalImportAccountId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      
      const { imported_count, skipped_count, skipped_duplicates, total_parsed, import_batch_id, message, errors } = response.data;
      
      if (imported_count > 0) {
        // Success - show with rollback option and duplicate details
        setLastImportBatchId(import_batch_id);
        
        // Build duplicate info if any
        let duplicateInfo = null;
        if (skipped_duplicates && skipped_duplicates.length > 0) {
          duplicateInfo = skipped_duplicates.map(d => 
            `${d.symbol} ${d.direction} ${d.entry_date} @ ₹${d.entry_price}`
          ).join(", ");
        }
        
        toast.success(
          <div>
            <p>{message}</p>
            {duplicateInfo && (
              <p className="text-xs text-slate-300 mt-1">Skipped: {duplicateInfo}</p>
            )}
            {import_batch_id && (
              <button 
                onClick={() => handleRollbackImport(import_batch_id)}
                className="mt-2 text-xs underline text-red-300 hover:text-red-200"
              >
                Undo Import
              </button>
            )}
          </div>,
          { duration: skipped_count > 0 ? 15000 : 10000 }
        );
        setShowHistoricalImportModal(false);
        setHistoricalImportFile(null);
        setHistoricalImportAccountId("");
        fetchTrades();
      } else if (total_parsed === 0) {
        // No trades found in file
        toast.error(
          <div>
            <p className="font-medium">No trades found in the Excel file</p>
            <p className="text-sm mt-1">Please check the file format. Each sheet should be named: Symbol-Strategy-Exchange</p>
          </div>,
          { duration: 8000 }
        );
      } else if (skipped_count > 0) {
        // All trades were duplicates - show details
        let duplicateInfo = null;
        if (skipped_duplicates && skipped_duplicates.length > 0) {
          duplicateInfo = skipped_duplicates.slice(0, 5).map(d => 
            `${d.symbol} ${d.entry_date} @ ₹${d.entry_price}`
          ).join(", ");
          if (skipped_duplicates.length > 5) {
            duplicateInfo += ` and ${skipped_duplicates.length - 5} more`;
          }
        }
        
        toast.warning(
          <div>
            <p className="font-medium">All {skipped_count} trades already exist</p>
            {duplicateInfo && (
              <p className="text-sm mt-1">{duplicateInfo}</p>
            )}
          </div>,
          { duration: 10000 }
        );
      } else if (errors && errors.length > 0) {
        // Errors occurred
        toast.error(
          <div>
            <p className="font-medium">Import failed with {errors.length} errors</p>
            <p className="text-sm mt-1">{errors[0]?.error || "Check the file format"}</p>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(message || "Import failed - unknown error");
      }
    } catch (error) {
      console.error("Error importing historical trades:", error);
      toast.error("Failed to import: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportingHistorical(false);
    }
  };

  const handleRollbackImport = async (batchId) => {
    if (!window.confirm(`Are you sure you want to undo this import? This will delete all trades from this batch.`)) {
      return;
    }
    
    setRollingBackBatch(batchId);
    try {
      const response = await api.delete(`/rollback-historical-import/${batchId}`);
      
      if (response.data.status) {
        toast.success(`Rolled back: ${response.data.deleted_count} trades deleted`);
        fetchTrades();
        // Refresh batches list
        fetchImportBatches();
        // Close detail view if open
        if (showImportLogDetail) {
          setShowImportLogDetail(false);
          setSelectedImportLog(null);
        }
      }
    } catch (error) {
      console.error("Error rolling back import:", error);
      toast.error("Rollback failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setRollingBackBatch(null);
    }
  };

  const fetchImportLogDetail = async (batchId) => {
    setLoadingImportLog(true);
    setShowImportLogDetail(true);
    try {
      const response = await api.get(`/historical-import-log/${batchId}`);
      if (response.data.status) {
        setSelectedImportLog(response.data.log);
      }
    } catch (error) {
      console.error("Error fetching import log:", error);
      toast.error("Failed to load import details");
      setShowImportLogDetail(false);
    } finally {
      setLoadingImportLog(false);
    }
  };

  const fetchImportBatches = async () => {
    setLoadingBatches(true);
    try {
      const response = await api.get("/historical-import-batches");
      if (response.data.status) {
        setImportBatches(response.data.batches || []);
      }
    } catch (error) {
      console.error("Error fetching import batches:", error);
      toast.error("Failed to load import history");
    } finally {
      setLoadingBatches(false);
    }
  };

  const openImportBatchesModal = () => {
    setShowImportBatchesModal(true);
    fetchImportBatches();
  };

  // ============================================================
  // RECONCILE FUNCTIONS
  // ============================================================

  const openReconcileModal = async (trade) => {
    setLoadingReconcile(true);
    setShowReconcileModal(true);
    setReconcileData(null);
    setReconcileExitPrice("");
    setReconcileExitDate(formatDateTimeForInput());
    setReconcileExitComments("");

    try {
      const response = await api.post(`/reconcile-trade/${trade.id}`);
      
      if (response.data.status) {
        setReconcileData(response.data.data);
        
        // Priority for exit price:
        // 1. Actual exit price from trade book (when position is closed today)
        // 2. If manual entry required (trade closed on previous day), leave empty for user to fill
        // 3. Current market price (only if position is still open for reference)
        const actualExitPrice = response.data.data.actual_exit_price;
        const exitPriceSource = response.data.data.exit_price_source;
        const positionExists = response.data.data.position_exists;
        
        if (actualExitPrice) {
          // Exit price from trade book - use it
          setReconcileExitPrice(actualExitPrice);
        } else if (exitPriceSource === "manual_required") {
          // Position closed but no exit price found - user must enter manually
          setReconcileExitPrice("");
        } else if (positionExists) {
          // Position still open - show current price for reference
          const currentPrice = response.data.data.current_price;
          const brokerLtp = response.data.data.broker_position?.ltp;
          setReconcileExitPrice(currentPrice || brokerLtp || "");
        } else {
          setReconcileExitPrice("");
        }
        
        // If we have exit trade info, set the exit date from it
        const exitTradeInfo = response.data.data.exit_trade_info;
        if (exitTradeInfo?.time) {
          // Try to parse and format the exit time in local timezone
          try {
            const timeStr = exitTradeInfo.time;
            // Check if it's a full datetime or just time
            let exitDateTime;
            if (timeStr.includes('T') || timeStr.includes('-')) {
              // Full datetime string
              exitDateTime = new Date(timeStr);
            } else {
              // Time-only string (e.g., "12:59:01") - combine with today's date
              const today = new Date();
              const [hours, minutes, seconds] = timeStr.split(':').map(Number);
              exitDateTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours || 0, minutes || 0, seconds || 0);
            }
            if (!isNaN(exitDateTime.getTime())) {
              setReconcileExitDate(formatDateTimeForInput(exitDateTime));
            }
          } catch (e) {
            console.error("Error parsing exit time:", e);
            // Keep default date
          }
        } else if (exitPriceSource === "manual_required") {
          // Clear the date if manual entry is required
          setReconcileExitDate("");
        }
      } else {
        toast.error(response.data.message || "Failed to reconcile");
        setShowReconcileModal(false);
      }
    } catch (error) {
      console.error("Error reconciling trade:", error);
      toast.error("Failed to reconcile: " + (error.response?.data?.detail || error.message));
      setShowReconcileModal(false);
    } finally {
      setLoadingReconcile(false);
    }
  };

  const handleCloseFromReconcile = async () => {
    if (!reconcileData || !reconcileExitPrice) {
      toast.error("Please enter an exit price");
      return;
    }

    if (!reconcileExitDate) {
      toast.error("Please enter an exit date");
      return;
    }

    setClosingFromReconcile(true);
    try {
      const response = await api.post(`/close-trade-from-broker/${reconcileData.trade.id}`, {
        trade_id: reconcileData.trade.id,
        exit_price: parseFloat(reconcileExitPrice),
        exit_date: reconcileExitDate,
        exit_comments: reconcileExitComments || "Closed via broker reconciliation"
      });

      if (response.data.status) {
        toast.success(response.data.message);
        setShowReconcileModal(false);
        fetchTrades();
      } else {
        toast.error("Failed to close trade");
      }
    } catch (error) {
      console.error("Error closing trade from reconcile:", error);
      toast.error("Failed to close: " + (error.response?.data?.detail || error.message));
    } finally {
      setClosingFromReconcile(false);
    }
  };

  // Handle sort for open trades
  const handleOpenSort = (key) => {
    setOpenSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setOpenTradesPage(1); // Reset to first page on sort change
  };

  // Handle sort for closed trades
  const handleClosedSort = (key) => {
    setClosedSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setClosedTradesPage(1); // Reset to first page on sort change
  };

  // Get sort icon for column header
  const getSortIcon = (key, sortConfig) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-400" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-400" />;
  };

  // Memoized filtered and sorted open trades
  const openTrades = useMemo(() => {
    let filtered = trades
      .filter((trade) => trade && trade.status === "OPEN")
      .filter((trade) => symbolFilter === "all" || trade.symbol === symbolFilter)
      .filter((trade) => directionFilter === "all" || trade.direction === directionFilter)
      .filter((trade) => accountFilter === "all" || trade.account_id === accountFilter)
      .filter((trade) => strategyFilter === "all" || trade.strategy_id === strategyFilter);
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (openSortConfig.key) {
        case 'symbol': aVal = (a.symbol || '').toLowerCase(); bVal = (b.symbol || '').toLowerCase(); break;
        case 'direction': aVal = a.direction || ''; bVal = b.direction || ''; break;
        case 'entry_date': aVal = new Date(a.entry_date || 0); bVal = new Date(b.entry_date || 0); break;
        case 'entry_price': aVal = a.entry_price || 0; bVal = b.entry_price || 0; break;
        case 'qty': aVal = (a.num_lots || 0) * (a.lot_size || 0); bVal = (b.num_lots || 0) * (b.lot_size || 0); break;
        default: aVal = a[openSortConfig.key] || ''; bVal = b[openSortConfig.key] || '';
      }
      if (aVal < bVal) return openSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return openSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [trades, symbolFilter, directionFilter, accountFilter, strategyFilter, openSortConfig]);

  // Memoized filtered and sorted closed trades (default: exit_date descending)
  const closedTrades = useMemo(() => {
    let filtered = trades
      .filter((trade) => trade && trade.status === "CLOSED")
      .filter((trade) => symbolFilter === "all" || trade.symbol === symbolFilter)
      .filter((trade) => directionFilter === "all" || trade.direction === directionFilter)
      .filter((trade) => accountFilter === "all" || trade.account_id === accountFilter)
      .filter((trade) => strategyFilter === "all" || trade.strategy_id === strategyFilter);
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (closedSortConfig.key) {
        case 'symbol': aVal = (a.symbol || '').toLowerCase(); bVal = (b.symbol || '').toLowerCase(); break;
        case 'direction': aVal = a.direction || ''; bVal = b.direction || ''; break;
        case 'entry_date': aVal = new Date(a.entry_date || 0); bVal = new Date(b.entry_date || 0); break;
        case 'entry_price': aVal = a.entry_price || 0; bVal = b.entry_price || 0; break;
        case 'exit_date': aVal = new Date(a.exit_date || 0); bVal = new Date(b.exit_date || 0); break;
        case 'exit_price': aVal = a.exit_price || 0; bVal = b.exit_price || 0; break;
        case 'pnl': aVal = a.pnl_amount || 0; bVal = b.pnl_amount || 0; break;
        case 'qty': aVal = (a.num_lots || 0) * (a.lot_size || 0); bVal = (b.num_lots || 0) * (b.lot_size || 0); break;
        default: aVal = a[closedSortConfig.key] || ''; bVal = b[closedSortConfig.key] || '';
      }
      if (aVal < bVal) return closedSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return closedSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [trades, symbolFilter, directionFilter, accountFilter, strategyFilter, closedSortConfig]);

  // Paginated trades
  const paginatedOpenTrades = useMemo(() => {
    const start = (openTradesPage - 1) * tradesPerPage;
    return openTrades.slice(start, start + tradesPerPage);
  }, [openTrades, openTradesPage, tradesPerPage]);

  const paginatedClosedTrades = useMemo(() => {
    const start = (closedTradesPage - 1) * tradesPerPage;
    return closedTrades.slice(start, start + tradesPerPage);
  }, [closedTrades, closedTradesPage, tradesPerPage]);

  const totalOpenPages = Math.ceil(openTrades.length / tradesPerPage);
  const totalClosedPages = Math.ceil(closedTrades.length / tradesPerPage);

  // Memoized unique symbols - sorted alphabetically
  const uniqueSymbols = useMemo(() => 
    [...new Set(trades.map(trade => trade.symbol).filter(Boolean))].sort((a, b) => (a || '').localeCompare(b || '')), 
    [trades]
  );

  const renderTradesTable = (tradesList, paginatedList, emptyMessage, isOpenTab = false, currentPage, totalPages, setPage, totalCount) => {
    const sortConfig = isOpenTab ? openSortConfig : closedSortConfig;
    const handleSort = isOpenTab ? handleOpenSort : handleClosedSort;
    
    if (tradesList.length === 0) {
      return (
        <div className="p-12 bg-charcoal/50 border border-white/10 rounded-md text-center">
          <p className="text-slate-300 text-lg">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="bg-charcoal/50 border border-white/10 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 bg-black/20">
                <th 
                  className="py-3 px-4 text-left cursor-pointer hover:text-slate-300 select-none"
                  onClick={() => handleSort('symbol')}
                >
                  <span className="flex items-center">Symbol {getSortIcon('symbol', sortConfig)}</span>
                </th>
                <th 
                  className="py-3 px-4 text-left cursor-pointer hover:text-slate-300 select-none"
                  onClick={() => handleSort('direction')}
                >
                  <span className="flex items-center">Direction {getSortIcon('direction', sortConfig)}</span>
                </th>
                <th 
                  className="py-3 px-4 text-left cursor-pointer hover:text-slate-300 select-none"
                  onClick={() => handleSort('entry_date')}
                >
                  <span className="flex items-center">Entry Date {getSortIcon('entry_date', sortConfig)}</span>
                </th>
                <th 
                  className="py-3 px-4 text-right cursor-pointer hover:text-slate-300 select-none"
                  onClick={() => handleSort('entry_price')}
                >
                  <span className="flex items-center justify-end">Entry Price {getSortIcon('entry_price', sortConfig)}</span>
                </th>
                {isOpenTab && <th className="py-3 px-4 text-right">Current Price</th>}
                <th 
                  className="py-3 px-4 text-right cursor-pointer hover:text-slate-300 select-none"
                  onClick={() => handleSort('qty')}
                >
                  <span className="flex items-center justify-end">Qty (Lots × Size) {getSortIcon('qty', sortConfig)}</span>
                </th>
                {!isOpenTab && (
                  <th 
                    className="py-3 px-4 text-left cursor-pointer hover:text-slate-300 select-none"
                    onClick={() => handleSort('exit_date')}
                  >
                    <span className="flex items-center">Exit Date {getSortIcon('exit_date', sortConfig)}</span>
                  </th>
                )}
                {!isOpenTab && (
                  <th 
                    className="py-3 px-4 text-right cursor-pointer hover:text-slate-300 select-none"
                    onClick={() => handleSort('exit_price')}
                  >
                    <span className="flex items-center justify-end">Exit Price {getSortIcon('exit_price', sortConfig)}</span>
                  </th>
                )}
                <th 
                  className="py-3 px-4 text-right cursor-pointer hover:text-slate-300 select-none"
                  onClick={() => handleSort('pnl')}
                >
                  <span className="flex items-center justify-end">{isOpenTab ? "SL / SAR" : "P&L"} {getSortIcon('pnl', sortConfig)}</span>
                </th>
                <th className="py-3 px-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedList.map((trade) => {
                const priceKey = `${trade.exchange}-${trade.symbol}`;
                const currentPrice = marketPrices[priceKey];
                const unrealizedPnL = isOpenTab ? calculateUnrealizedPnL(trade) : null;
                const slStatus = isOpenTab ? evaluateSlStatus(trade, currentPrice) : null;

                return (
                  <tr
                    key={trade.id}
                    data-testid={`trade-row-${trade.id}`}
                    data-sl-status={slStatus || undefined}
                    data-rollover-pending={trade.rollover_pending ? "true" : undefined}
                    className={`trade-table-row border-b border-white/5 hover:bg-white/5 cursor-pointer ${
                      trade.rollover_pending
                        ? "bg-red-500/15 hover:bg-red-500/20 border-l-2 border-l-red-500"
                        : slStatus === "missed"
                          ? "bg-yellow-500/15 hover:bg-yellow-500/20 border-l-2 border-l-yellow-400"
                          : ""
                    }`}
                    onDoubleClick={() => {
                      // Preserve ALL filter state in navigation
                      const params = new URLSearchParams();
                      if (accountFilter && accountFilter !== "all") params.set("account", accountFilter);
                      if (symbolFilter && symbolFilter !== "all") params.set("symbol", symbolFilter);
                      if (directionFilter && directionFilter !== "all") params.set("direction", directionFilter);
                      if (activeTab && activeTab !== "open") params.set("tab", activeTab);
                      if (tradesPerPage !== 20) params.set("perPage", tradesPerPage.toString());
                      if (isOpenTab && openTradesPage > 1) params.set("openPage", openTradesPage.toString());
                      if (!isOpenTab && closedTradesPage > 1) params.set("closedPage", closedTradesPage.toString());
                      const returnUrl = params.toString() ? `/trades?${params.toString()}` : "/trades";
                      navigate(`/edit-trade/${trade.id}`, { state: { returnUrl } });
                    }}
                    title="Double-click to edit"
                  >
                    <td className="py-3 px-4 min-w-[180px]">
                      <div className="text-sm text-white font-medium flex items-center gap-1.5">
                        <span>{trade.symbol}</span>
                        {isOpenTab && (
                          <button
                            data-testid={`chart-btn-${trade.id}`}
                            onClick={(e) => { e.stopPropagation(); openTradeChart(trade); }}
                            className="text-slate-500 hover:text-cyan-400 transition-colors"
                            title="View live chart with Entry / SL / Target levels"
                          >
                            <CandlestickChart className="w-4 h-4" />
                          </button>
                        )}
                        <AutoSyncBadges trade={trade} />
                      </div>
                      <div className="text-xs text-slate-500">
                        {trade.exchange}
                        {trade.account_id && (
                          <span className="ml-1 text-blue-400">· {getAccountName(trade.account_id)}</span>
                        )}
                        {trade.contract_expiry && (
                          <span className={`ml-1 ${trade.rollover_pending ? "text-red-400 font-semibold" : "text-yellow-500"}`}>
                            ({trade.contract_expiry})
                          </span>
                        )}
                        {trade.rollover_pending && (
                          <span
                            data-testid={`rollover-pending-pill-${trade.id}`}
                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-300 border border-red-500/40"
                            title="SpeedBot's expected rollover cut-off (14:10 IST on the rollover trading day) has passed — position is still on the current contract."
                          >
                            ● Rollover Pending
                          </span>
                        )}
                      </div>
                      {(trade.speedbot_bot_name || trade.strategy_name) && (
                        <div
                          className="text-[11px] text-violet-300/80 mt-0.5 flex items-center gap-1.5"
                          title={
                            trade.speedbot_bot_status === "ACTIVE"
                              ? "SpeedBot bot is running"
                              : trade.speedbot_bot_status === "INACTIVE"
                                ? "SpeedBot bot is inactive — check SpeedBot Live"
                                : "SpeedBot bot (status unknown)"
                          }
                        >
                          {trade.speedbot_bot_status && (
                            <span
                              data-testid={`bot-status-dot-${trade.id}`}
                              className={`w-1.5 h-1.5 rounded-full inline-block ${
                                trade.speedbot_bot_status === "ACTIVE"
                                  ? "bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.8)]"
                                  : "bg-rose-500 shadow-[0_0_4px_rgba(244,63,94,0.8)]"
                              }`}
                            />
                          )}
                          <span className="truncate max-w-[220px]">
                            {trade.speedbot_bot_name || trade.strategy_name}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`flex items-center gap-1 ${
                        trade.direction === "LONG" ? "text-profit" : "text-loss"
                      }`}>
                        {trade.direction === "LONG" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {trade.direction}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-300 font-mono">
                      {formatDateIST(trade.entry_date)}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-300 font-mono text-right">
                      ₹{trade.entry_price?.toLocaleString('en-IN')}
                    </td>
                    {isOpenTab && (
                      <td className="py-3 px-4 text-sm font-mono text-right">
                        {editingPrice === trade.id ? (
                          <div className="flex items-center gap-2 justify-end">
                            <Input
                              type="number"
                              step="0.01"
                              value={manualPrice}
                              onChange={(e) => setManualPrice(e.target.value)}
                              className="w-28 bg-black/20 border-white/10 text-white text-sm font-mono h-7 px-2"
                              autoFocus
                            />
                            <button onClick={() => saveManualPrice(trade)} className="p-1 hover:bg-profit/20 rounded text-profit">
                              <Check className="w-3 h-3" />
                            </button>
                            <button onClick={cancelManualPrice} className="p-1 hover:bg-loss/20 rounded text-loss">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {currentPrice !== undefined && currentPrice !== null ? (
                              <>
                                <span className="text-white">₹{currentPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                <button onClick={() => handleManualPriceUpdate(trade)} className="p-1 hover:bg-white/10 rounded text-slate-400">
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              </>
                            ) : (
                              <button onClick={() => handleManualPriceUpdate(trade)} className="text-blue-400 hover:text-blue-300 text-xs">
                                {priceLoading ? '...' : 'Set'}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="py-3 px-4 text-sm text-slate-300 font-mono text-right">
                      <div className="flex flex-col items-end">
                        <span>{(trade.num_lots || 0)} × {(trade.lot_size || 1)}</span>
                        {/* Show exit legs indicator if partial closes exist */}
                        {trade.exit_legs && trade.exit_legs.length > 0 && (
                          <span className="text-xs text-yellow-400" title={`${trade.exit_legs.length} partial exit(s)`}>
                            {trade.remaining_lots ?? trade.num_lots} lots open
                          </span>
                        )}
                        {trade.partial_pnl_amount && (
                          <span className={`text-xs ${trade.partial_pnl_amount >= 0 ? 'text-profit' : 'text-loss'}`}>
                            Booked: ₹{trade.partial_pnl_amount.toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>
                    </td>
                    {!isOpenTab && (
                      <td className="py-3 px-4 text-sm text-slate-300 font-mono">
                        {formatDateIST(trade.exit_date)}
                      </td>
                    )}
                    {!isOpenTab && (
                      <td className="py-3 px-4 text-sm text-slate-300 font-mono text-right">
                        {trade.exit_price ? `₹${trade.exit_price.toLocaleString('en-IN')}` : "-"}
                      </td>
                    )}
                    <td className="py-3 px-4 text-sm font-mono text-right font-semibold">
                      {isOpenTab ? (
                        (() => {
                          // Stale-SL guard: when an SL row's last refresh
                          // predates the trade's entry, it belonged to a
                          // prior closed position on the same symbol. Show
                          // a refresh-pending placeholder instead of the
                          // stale price so the user isn't misled.
                          let isStaleSl = false;
                          if (trade.sl_price && trade.sl_checked_at && trade.entry_date) {
                            const ca = new Date(trade.sl_checked_at).getTime();
                            const ea = new Date(trade.entry_date).getTime();
                            if (Number.isFinite(ca) && Number.isFinite(ea) && ca < ea) {
                              isStaleSl = true;
                            }
                          }
                          if (trade.sl_price && !isStaleSl) {
                            return (
                          <div
                            data-testid={`sl-cell-${trade.id}`}
                            className="flex flex-col items-end gap-0.5"
                            title={
                              slStatus === "missed"
                                ? "⚠ CMP has crossed SL — trade still open (missed stop)"
                                : slStatus === "near"
                                  ? `CMP is within ${SL_NEAR_THRESHOLD_PCT}% of SL`
                                  : "SL level from SpeedBot"
                            }
                          >
                            <div className="flex items-center gap-1.5">
                              {slStatus === "missed" && (
                                <span data-testid={`sl-missed-badge-${trade.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-500 text-black">MISSED</span>
                              )}
                              {slStatus === "near" && (
                                <span data-testid={`sl-near-dot-${trade.id}`} className="w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)]" />
                              )}
                              <span className={
                                slStatus === "missed" ? "text-yellow-200" :
                                slStatus === "near"   ? "text-amber-300" :
                                                        "text-slate-200"
                              }>
                                ₹{Number(trade.sl_price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            {trade.sl_order_type && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-400/20">
                                {trade.sl_order_type}
                              </span>
                            )}
                          </div>
                            );
                          }
                          if (isStaleSl) {
                            return (
                              <span
                                data-testid={`sl-pending-${trade.id}`}
                                className="text-slate-500 text-xs italic"
                                title="Previous SL belonged to a closed position. Awaiting fresh SL/SAR from the next SpeedBot fetch."
                              >
                                pending…
                              </span>
                            );
                          }
                          return (
                            <span className="text-slate-600 text-xs" title="No SL on file — run 'Fetch from SpeedBot' on the Signals page">—</span>
                          );
                        })()
                      ) : trade.pnl_amount !== undefined && trade.pnl_amount !== null ? (
                        <span className={(trade.pnl_amount || 0) > 0 ? "text-profit" : "text-loss"}>
                          ₹{(trade.pnl_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        {isOpenTab && (
                          <>
                            <button
                              data-testid={`reconcile-trade-${trade.id}`}
                              onClick={() => openReconcileModal(trade)}
                              className="px-2 py-1 text-xs bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded"
                              title="Reconcile with Broker"
                            >
                              <GitCompare className="w-3 h-3 inline mr-1" />
                              Reconcile
                            </button>
                            <button
                              data-testid={`close-trade-${trade.id}`}
                              onClick={() => openCloseTradeModal(trade)}
                              className="px-2 py-1 text-xs bg-loss/20 hover:bg-loss/30 text-loss rounded"
                              title="Close Trade"
                            >
                              Close
                            </button>
                            {/* Only show Partial Close for trades with more than 1 lot */}
                            {(trade.num_lots > 1 || (trade.remaining_lots && trade.remaining_lots > 1)) && (
                              <button
                                data-testid={`partial-close-${trade.id}`}
                                onClick={() => openPartialCloseModal(trade)}
                                className="px-2 py-1 text-xs bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded"
                                title="Partial Close - Exit some lots"
                              >
                                Partial
                              </button>
                            )}
                            <button
                              data-testid={`rollover-trade-${trade.id}`}
                              onClick={() => openRolloverModal(trade)}
                              className="px-2 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded"
                              title="Rollover to next period"
                            >
                              Rollover
                            </button>
                          </>
                        )}
                        <button
                          data-testid={`edit-trade-${trade.id}`}
                          onClick={() => {
                            // Preserve ALL filter state in navigation
                            const params = new URLSearchParams();
                            if (accountFilter && accountFilter !== "all") params.set("account", accountFilter);
                            if (symbolFilter && symbolFilter !== "all") params.set("symbol", symbolFilter);
                            if (directionFilter && directionFilter !== "all") params.set("direction", directionFilter);
                            if (activeTab && activeTab !== "open") params.set("tab", activeTab);
                            if (tradesPerPage !== 20) params.set("perPage", tradesPerPage.toString());
                            if (isOpenTab && openTradesPage > 1) params.set("openPage", openTradesPage.toString());
                            if (!isOpenTab && closedTradesPage > 1) params.set("closedPage", closedTradesPage.toString());
                            const returnUrl = params.toString() ? `/trades?${params.toString()}` : "/trades";
                            navigate(`/edit-trade/${trade.id}`, { state: { returnUrl } });
                          }}
                          className="p-1.5 hover:bg-white/10 rounded text-slate-400 hover:text-white"
                          title="Edit entry details"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`delete-trade-${trade.id}`}
                          onClick={() => {
                            setTradeToDelete(trade.id);
                            setDeleteDialogOpen(true);
                          }}
                          className="p-1.5 hover:bg-red-900/30 rounded text-slate-400 hover:text-loss"
                          title="Delete trade"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls - Always show if there are trades */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-black/20">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <span>Showing {((currentPage - 1) * tradesPerPage) + 1}-{Math.min(currentPage * tradesPerPage, totalCount)} of {totalCount}</span>
              <Select value={tradesPerPage.toString()} onValueChange={(v) => { setTradesPerPage(parseInt(v)); setPage(1); }}>
                <SelectTrigger className="w-20 h-8 bg-black/20 border-white/10 text-white text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border-white/10">
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span>per page</span>
            </div>
            
            {/* Only show page navigation if more than 1 page */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                {/* Previous button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0 border-white/10 text-slate-300 hover:bg-white/10 disabled:opacity-30"
                >
                  <ArrowUp className="w-4 h-4 rotate-[-90deg]" />
                </Button>
                
                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const showEllipsisStart = currentPage > 3;
                  const showEllipsisEnd = currentPage < totalPages - 2;
                  
                  // Always show first page
                  pages.push(1);
                  
                  if (showEllipsisStart) {
                    pages.push('...');
                }
                
                // Show pages around current
                for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                  if (!pages.includes(i)) pages.push(i);
                }
                
                if (showEllipsisEnd) {
                  pages.push('...');
                }
                
                // Always show last page if more than 1 page
                if (totalPages > 1 && !pages.includes(totalPages)) {
                  pages.push(totalPages);
                }
                
                return pages.map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-slate-500">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPage(page)}
                      className={`h-8 w-8 p-0 ${
                        currentPage === page 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'border-white/10 text-slate-300 hover:bg-white/10'
                      }`}
                    >
                      {page}
                    </Button>
                  )
                ));
              })()}
              
              {/* Next button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0 border-white/10 text-slate-300 hover:bg-white/10 disabled:opacity-30"
              >
                <ArrowUp className="w-4 h-4 rotate-90" />
              </Button>
            </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400 font-mono">Loading trades...</div>
      </div>
    );
  }

  return (
    <div data-testid="trades-list-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold tracking-tight text-4xl sm:text-5xl text-white mb-2">
            All Trades
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-slate-400 text-base">View and manage your trading history</p>
            {marketStatus && (
              <div data-testid="market-status-pills" className="flex items-center gap-2">
                {["NSE", "MCX"].map((ex) => {
                  const m = marketStatus[ex] || {};
                  const isOpen = !!m.open;
                  return (
                    <span
                      key={ex}
                      data-testid={`market-pill-${ex}`}
                      title={m.reason || (isOpen ? `${ex} market is open` : `${ex} market is closed`)}
                      className={`inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full border ${
                        isOpen
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                          : "border-zinc-700 bg-zinc-800/40 text-zinc-400"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          isOpen
                            ? "bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.8)]"
                            : "bg-zinc-500"
                        }`}
                      />
                      {ex}
                    </span>
                  );
                })}
                <button
                  data-testid="refresh-sl-btn"
                  disabled={refreshingSL}
                  onClick={async () => {
                    setRefreshingSL(true);
                    try {
                      // Two-pass refresh:
                      // 1. Run the live SpeedBot fetcher (covers credentialed accounts).
                      // 2. Patch any existing trade_signals onto open trades
                      //    (covers manual-upload accounts the fetcher can't reach).
                      const r1 = await api.post("/speedbot-api/signals/fetch-now");
                      const r2 = await api.post("/speedbot-api/signals/patch-trades");
                      const fetchedSignals = r1.data?.signals_created || 0;
                      const fetchPatched = r1.data?.trades_updated || 0;
                      const backfillPatched = r2.data?.trades_patched || 0;
                      const errors = r1.data?.errors || 0;
                      const totalPatched = fetchPatched + backfillPatched;
                      if (errors) {
                        toast.warning(`SL refresh: ${fetchedSignals} signals · ${totalPatched} trades patched · ${errors} error(s)`);
                      } else {
                        toast.success(`SL refresh OK — ${fetchedSignals} signals · ${totalPatched} trades patched`);
                      }
                      // Re-pull trades so the SL column updates inline.
                      fetchTrades();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "SL refresh failed");
                    } finally {
                      setRefreshingSL(false);
                    }
                  }}
                  title="Pull the latest Order-Sheet from SpeedBot for every credentialed account, replace `trade_signals`, and patch SL/SAR onto matching OPEN trades."
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {refreshingSL ? (
                    <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                  )}
                  {refreshingSL ? "Refreshing…" : "Refresh SL/SAR"}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="import-broker-button"
            onClick={openImportModal}
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-sm font-medium px-4 py-3 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Import from Broker
          </button>
          <button
            data-testid="import-historical-button"
            onClick={() => setShowHistoricalImportModal(true)}
            className="bg-purple-600 text-white hover:bg-purple-700 rounded-sm font-medium px-4 py-3 transition-colors flex items-center gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Import Historical
          </button>
          <button
            data-testid="add-trade-button"
            onClick={() => navigate("/add-trade")}
            className="bg-white text-black hover:bg-slate-200 rounded-sm font-medium px-6 py-3 transition-colors"
          >
            Add New Trade
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6 p-4 bg-charcoal/30 rounded-md border border-white/5">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-slate-500 text-sm">Filters:</span>
        </div>
        <div className="flex flex-wrap gap-4">
          <Select value={symbolFilter} onValueChange={setSymbolFilter}>
            <SelectTrigger className="w-48 bg-black/20 border-white/10 text-white text-sm h-9" data-testid="symbol-filter">
              <SelectValue placeholder="All Symbols" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              <SelectItem value="all">All Symbols</SelectItem>
              {uniqueSymbols.map((symbol) => (
                <SelectItem key={symbol} value={symbol}>{symbol}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={directionFilter} onValueChange={setDirectionFilter}>
            <SelectTrigger className="w-36 bg-black/20 border-white/10 text-white text-sm h-9" data-testid="direction-filter">
              <SelectValue placeholder="All Directions" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              <SelectItem value="all">All Directions</SelectItem>
              <SelectItem value="LONG">LONG</SelectItem>
              <SelectItem value="SHORT">SHORT</SelectItem>
            </SelectContent>
          </Select>

          <Select value={accountFilter} onValueChange={setAccountFilter}>
            <SelectTrigger className="w-44 bg-black/20 border-white/10 text-white text-sm h-9" data-testid="account-filter">
              <SelectValue placeholder="All Accounts" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              <SelectItem value="all">All Accounts</SelectItem>
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={strategyFilter} onValueChange={setStrategyFilter}>
            <SelectTrigger className="w-44 bg-black/20 border-white/10 text-white text-sm h-9" data-testid="strategy-filter">
              <SelectValue placeholder="All Strategies" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              <SelectItem value="all">All Strategies</SelectItem>
              {strategies.map((strategy) => (
                <SelectItem key={strategy.id} value={strategy.id}>{strategy.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 bg-charcoal/50">
          <TabsTrigger 
            value="open" 
            data-testid="open-trades-tab"
            className="data-[state=active]:bg-profit/20 data-[state=active]:text-profit"
          >
            Open Trades ({openTrades.length})
          </TabsTrigger>
          <TabsTrigger 
            value="closed" 
            data-testid="closed-trades-tab"
            className="data-[state=active]:bg-slate-500/20 data-[state=active]:text-white"
          >
            Closed Trades ({closedTrades.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="open" className="mt-6">
          {autoEnabledBots.length > 0 && (
            <div
              data-testid="auto-enabled-bots-banner"
              className="mb-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3"
            >
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-emerald-100">
                    {autoEnabledBots.length} new SpeedBot {autoEnabledBots.length > 1 ? "bots have" : "bot has"} been auto-enabled
                  </div>
                  <div className="text-xs text-emerald-100/70 mt-1 leading-relaxed">
                    These bots are now flowing to your Open Trades automatically. If any
                    of them are forward-test bots you don't want in your journal, click
                    <span className="font-semibold text-emerald-100"> Disable </span>
                    below. Dismiss buttons disappear after 24 h.
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {autoEnabledBots.map((b) => (
                      <div
                        key={`${b.account_id}-${b.bot_id}`}
                        data-testid={`auto-enabled-bot-${b.bot_id}`}
                        className="inline-flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1"
                      >
                        <span className="font-mono text-xs text-emerald-100">{b.bot_name}</span>
                        <span className="text-[10px] text-emerald-100/50">·</span>
                        <span className="text-[10px] text-emerald-100/60">{b.account_name}</span>
                        <button
                          data-testid={`disable-bot-${b.bot_id}`}
                          onClick={() => dismissAutoEnabledBot(b.account_id, b.bot_id)}
                          className="ml-1 text-[10px] font-semibold text-rose-300 hover:text-rose-200 hover:underline"
                          title="Disable this bot from auto-syncing to the journal"
                        >
                          Disable
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {inactiveBots.length > 0 && (
            <div
              data-testid="inactive-bots-banner"
              className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start justify-between gap-4 flex-wrap"
            >
              <div className="flex items-start gap-3 min-w-0">
                <AlertCircle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-amber-100">
                    {inactiveBots.length} SpeedBot {inactiveBots.length > 1 ? "bots are" : "bot is"} INACTIVE
                  </div>
                  <div className="text-xs text-amber-100/70 mt-0.5 leading-relaxed">
                    {inactiveBots.slice(0, 5).map((b, i) => (
                      <span key={`${b.account_id}-${b.bot_id}`} className="inline-block mr-3">
                        <span className="font-mono text-amber-200">{b.bot_name}</span>
                        <span className="text-amber-100/50"> · {b.account_name}</span>
                        {i < Math.min(inactiveBots.length, 5) - 1 ? "" : ""}
                      </span>
                    ))}
                    {inactiveBots.length > 5 && (
                      <span className="text-amber-100/60"> +{inactiveBots.length - 5} more</span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                data-testid="open-speedbot-btn"
                onClick={() => navigate("/speedbot")}
                className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold shrink-0"
                size="sm"
              >
                <Zap className="w-4 h-4 mr-2" /> Open SpeedBot
              </Button>
            </div>
          )}
          {renderTradesTable(openTrades, paginatedOpenTrades, "No open trades found. Start trading!", true, openTradesPage, totalOpenPages, setOpenTradesPage, openTrades.length)}
        </TabsContent>

        <TabsContent value="closed" className="mt-6">
          {renderTradesTable(closedTrades, paginatedClosedTrades, "No closed trades found.", false, closedTradesPage, totalClosedPages, setClosedTradesPage, closedTrades.length)}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-charcoal border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Trade</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete this trade? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-slate-300 hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-loss hover:bg-loss/80 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close Trade Modal */}
      <CloseTradeModal
        isOpen={showCloseModal}
        onClose={setShowCloseModal}
        trade={tradeToClose}
        closeForm={closeForm}
        setCloseForm={setCloseForm}
        onCloseTrade={handleCloseTrade}
        isClosing={closingTrade}
      />

      {/* Rollover Modal */}
      <RolloverModal
        isOpen={showRolloverModal}
        onClose={(open) => {
          setShowRolloverModal(open);
          if (!open) {
            setNextExpiryInfo(null);
          }
        }}
        trade={tradeToRollover}
        rolloverForm={rolloverForm}
        setRolloverForm={setRolloverForm}
        onRollover={handleRollover}
        isRollingOver={rollingOver}
        nextExpiryInfo={nextExpiryInfo}
        loadingNextExpiry={loadingNextExpiry}
      />

      {/* Partial Close Modal */}
      <PartialCloseModal
        isOpen={showPartialCloseModal}
        onClose={() => {
          setShowPartialCloseModal(false);
          setTradeToPartialClose(null);
        }}
        trade={tradeToPartialClose}
        onSuccess={handlePartialCloseSuccess}
      />

      {/* Import from Broker Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-charcoal border-white/10 text-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-400" />
              Import from Broker
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Import trades from your broker - choose Positions for current holdings, or Trade Book for today's completed trades
            </DialogDescription>
          </DialogHeader>
          
          {/* Account Selection - Always visible first */}
          <div className="px-1 py-3 border-b border-white/10">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <Label className="text-slate-300 text-sm mb-2 block font-medium">
                  Select Broker Account *
                </Label>
                <Select value={importAccountId} onValueChange={handleAccountSelectForImport}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-white" data-testid="import-account-select">
                    <SelectValue placeholder="Select Account to Import From" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    {accounts.filter(a => a.broker_credentials?.api_key).length === 0 ? (
                      <SelectItem value="no-accounts" disabled>
                        No accounts with broker credentials configured
                      </SelectItem>
                    ) : (
                      accounts.filter(a => a.broker_credentials?.api_key).map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name} {account.broker && `(${account.broker})`}
                          {account.broker_credentials?.is_connected && " ✓"}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Mode Tabs */}
              {importAccountId && importAccountId !== "none" && (
                <Tabs value={importMode} onValueChange={handleImportModeChange} className="w-auto">
                  <TabsList className="bg-black/30 border border-white/10">
                    <TabsTrigger 
                      value="positions" 
                      className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                      data-testid="import-positions-tab"
                    >
                      Open Positions
                    </TabsTrigger>
                    <TabsTrigger 
                      value="tradebook" 
                      className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                      data-testid="import-tradebook-tab"
                    >
                      Trade Book
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </div>
            
            {accounts.filter(a => a.broker_credentials?.api_key).length === 0 && (
              <p className="text-yellow-500 text-xs mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                No accounts with broker credentials. <a href="/accounts" className="text-blue-400 hover:underline">Set up an account</a>
              </p>
            )}
            
            {importMode === "tradebook" && importAccountId && (
              <p className="text-purple-400 text-xs mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Trade Book shows today's executed trades with entry-exit correlation. Use this for multiple trades on the same symbol.
              </p>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto py-4">
            {!importAccountId || importAccountId === "none" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="w-12 h-12 text-slate-500 mb-3" />
                <p className="text-slate-300 text-lg mb-2">Select an Account</p>
                <p className="text-slate-500 text-sm">Choose a broker account above to fetch data</p>
              </div>
            ) : importMode === "positions" ? (
              /* === POSITIONS MODE === */
              loadingPositions ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin mb-3" />
                <p className="text-slate-400">Connecting to broker and fetching positions...</p>
              </div>
            ) : brokerPositions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="w-12 h-12 text-slate-500 mb-3" />
                <p className="text-slate-300 text-lg mb-2">No Active Positions Found</p>
                <p className="text-slate-500 text-sm">There are no open positions in this broker account</p>
                <Button 
                  variant="outline" 
                  onClick={() => fetchBrokerPositions(importAccountId)}
                  className="mt-4 border-white/10 text-slate-300"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Select All */}
                <div className="flex items-center justify-between px-2">
                  <button 
                    onClick={selectAllPositions}
                    className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-2"
                  >
                    <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                      selectedPositions.length === brokerPositions.filter(p => !p.already_in_journal).length && selectedPositions.length > 0
                        ? 'bg-blue-500 border-blue-500' 
                        : 'border-slate-500'
                    }`}>
                      {selectedPositions.length === brokerPositions.filter(p => !p.already_in_journal).length && selectedPositions.length > 0 && <Check className="w-3 h-3 text-white" />}
                    </div>
                    Select All New ({brokerPositions.filter(p => !p.already_in_journal).length} of {brokerPositions.length})
                  </button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => fetchBrokerPositions(importAccountId)}
                    className="text-slate-400 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Refresh
                  </Button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-profit/10 border border-profit/30 p-3 rounded-md">
                    <p className="text-profit text-2xl font-bold">
                      {brokerPositions.filter(p => !p.already_in_journal && p.direction === "LONG").length}
                    </p>
                    <p className="text-slate-400 text-xs uppercase tracking-wider">Long Positions</p>
                  </div>
                  <div className="bg-loss/10 border border-loss/30 p-3 rounded-md">
                    <p className="text-loss text-2xl font-bold">
                      {brokerPositions.filter(p => !p.already_in_journal && p.direction === "SHORT").length}
                    </p>
                    <p className="text-slate-400 text-xs uppercase tracking-wider">Short Positions</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-md">
                    <p className="text-blue-400 text-2xl font-bold">
                      {brokerPositions.filter(p => p.already_in_journal).length}
                    </p>
                    <p className="text-slate-400 text-xs uppercase tracking-wider">Already in Journal</p>
                  </div>
                </div>

                {/* Positions List */}
                <div className="space-y-2">
                  {brokerPositions.filter(p => !p.already_in_journal).map((pos) => (
                    <div 
                      key={pos.tradingsymbol}
                      onClick={() => togglePositionSelection(pos)}
                      className={`p-3 rounded-md border transition-all ${
                        selectedPositions.includes(pos.tradingsymbol) 
                          ? 'bg-blue-900/30 border-blue-500/50 cursor-pointer' 
                          : 'bg-black/20 border-white/10 hover:border-white/20 cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                          selectedPositions.includes(pos.tradingsymbol) 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-slate-500'
                        }`}>
                          {selectedPositions.includes(pos.tradingsymbol) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        
                        <div className="flex-1 grid grid-cols-5 gap-3 items-center">
                          <div>
                            <p className="text-white font-medium text-sm">{pos.base_symbol}</p>
                            <p className="text-slate-500 text-xs">{pos.tradingsymbol}</p>
                            <p className="text-slate-600 text-xs">{pos.exchange}</p>
                          </div>
                          
                          <div className="text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              pos.direction === "LONG" ? 'bg-profit/20 text-profit' : 'bg-loss/20 text-loss'
                            }`}>
                              {pos.direction}
                            </span>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-white font-mono text-sm">₹{pos.avgnetprice?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                            <p className="text-slate-500 text-xs">
                              {pos.entry_price_source === "TRADE_BOOK" ? (
                                <span className="text-profit">Entry Price</span>
                              ) : (
                                <span>Avg Price (ATP)</span>
                              )}
                            </p>
                            {pos.atp && pos.atp !== pos.avgnetprice && (
                              <p className="text-slate-600 text-[10px]">ATP: ₹{pos.atp?.toLocaleString('en-IN')}</p>
                            )}
                          </div>
                          
                          <div className="text-right">
                            <p className="text-white font-mono text-sm">{Math.abs(pos.netqty)}</p>
                            <p className="text-slate-500 text-xs">{pos.num_lots} lots × {pos.suggested_lot_size}</p>
                          </div>
                          
                          <div className="text-right">
                            <p className={`font-mono text-sm ${pos.unrealised >= 0 ? 'text-profit' : 'text-loss'}`}>
                              ₹{pos.unrealised?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </p>
                            <p className="text-slate-500 text-xs">Unrealized P&L</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
            ) : (
              /* === TRADE BOOK MODE === */
              loadingTradeBook ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-purple-400 animate-spin mb-3" />
                  <p className="text-slate-400">Fetching today's trade book...</p>
                </div>
              ) : tradeBookTrades.length === 0 && orphanExits.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <AlertCircle className="w-12 h-12 text-slate-500 mb-3" />
                  <p className="text-slate-300 text-lg mb-2">No Trades Found Today</p>
                  <p className="text-slate-500 text-sm">There are no executed trades in today's trade book</p>
                  <Button 
                    variant="outline" 
                    onClick={() => fetchTradeBook(importAccountId)}
                    className="mt-4 border-white/10 text-slate-300"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Only show new trades section if there are any */}
                  {tradeBookTrades.length > 0 && (
                    <>
                  {/* Select All */}
                  <div className="flex items-center justify-between px-2">
                    <button 
                      onClick={selectAllTradeBookTrades}
                      className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-2"
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                        selectedTradeBookTrades.length === tradeBookTrades.filter(t => t.action === "import_new" || t.action === "close_existing").length && selectedTradeBookTrades.length > 0
                          ? 'bg-purple-500 border-purple-500' 
                          : 'border-slate-500'
                      }`}>
                        {selectedTradeBookTrades.length === tradeBookTrades.filter(t => t.action === "import_new" || t.action === "close_existing").length && selectedTradeBookTrades.length > 0 && <Check className="w-3 h-3 text-white" />}
                      </div>
                      Select All Actionable ({tradeBookTrades.filter(t => t.action === "import_new" || t.action === "close_existing").length} of {tradeBookTrades.length})
                    </button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => fetchTradeBook(importAccountId)}
                      className="text-slate-400 hover:text-white"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-5 gap-3 mb-4">
                    <div className="bg-purple-500/10 border border-purple-500/30 p-3 rounded-md">
                      <p className="text-purple-400 text-2xl font-bold">
                        {tradeBookTrades.filter(t => t.action === "import_new").length}
                      </p>
                      <p className="text-slate-400 text-xs uppercase tracking-wider">New</p>
                    </div>
                    <div className="bg-orange-500/10 border border-orange-500/30 p-3 rounded-md">
                      <p className="text-orange-400 text-2xl font-bold">
                        {tradeBookTrades.filter(t => t.action === "close_existing").length}
                      </p>
                      <p className="text-slate-400 text-xs uppercase tracking-wider">Can Close</p>
                    </div>
                    <div className="bg-profit/10 border border-profit/30 p-3 rounded-md">
                      <p className="text-profit text-2xl font-bold">
                        {tradeBookTrades.filter(t => t.direction === "LONG").length}
                      </p>
                      <p className="text-slate-400 text-xs uppercase tracking-wider">Long</p>
                    </div>
                    <div className="bg-loss/10 border border-loss/30 p-3 rounded-md">
                      <p className="text-loss text-2xl font-bold">
                        {tradeBookTrades.filter(t => t.direction === "SHORT").length}
                      </p>
                      <p className="text-slate-400 text-xs uppercase tracking-wider">Short</p>
                    </div>
                    <div className="bg-slate-500/10 border border-slate-500/30 p-3 rounded-md">
                      <p className="text-slate-400 text-2xl font-bold">
                        {tradeBookTrades.filter(t => t.already_in_journal && t.action === "none").length}
                      </p>
                      <p className="text-slate-400 text-xs uppercase tracking-wider">Imported</p>
                    </div>
                  </div>

                  {/* Trades List */}
                  <div className="space-y-2">
                    {tradeBookTrades.map((trade, idx) => {
                      const key = `${trade.tradingsymbol}_${trade.entry_time}_${idx}`;
                      // Determine display state based on action
                      const isFullyImported = trade.already_in_journal && trade.action === "none";
                      const canCloseExisting = trade.action === "close_existing";
                      const canImportNew = trade.action === "import_new";
                      const isSelectable = canCloseExisting || canImportNew;
                      
                      return (
                        <div 
                          key={key}
                          onClick={() => isSelectable && toggleTradeBookSelection(trade, idx)}
                          className={`p-3 rounded-md border transition-all ${
                            isFullyImported
                              ? 'bg-slate-800/30 border-slate-600/30 cursor-not-allowed opacity-60'
                              : canCloseExisting
                                ? selectedTradeBookTrades.includes(key)
                                  ? 'bg-orange-900/30 border-orange-500/50 cursor-pointer'
                                  : 'bg-orange-900/10 border-orange-500/30 hover:border-orange-500/50 cursor-pointer'
                                : selectedTradeBookTrades.includes(key) 
                                  ? 'bg-purple-900/30 border-purple-500/50 cursor-pointer' 
                                  : 'bg-black/20 border-white/10 hover:border-white/20 cursor-pointer'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                              isFullyImported
                                ? 'bg-slate-500 border-slate-500'
                                : canCloseExisting
                                  ? selectedTradeBookTrades.includes(key)
                                    ? 'bg-orange-500 border-orange-500'
                                    : 'border-orange-400'
                                  : selectedTradeBookTrades.includes(key) 
                                    ? 'bg-purple-500 border-purple-500' 
                                    : 'border-slate-500'
                            }`}>
                              {isFullyImported ? (
                                <Check className="w-3 h-3 text-white" />
                              ) : (
                                selectedTradeBookTrades.includes(key) && <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            
                            <div className="flex-1 grid grid-cols-6 gap-2 items-center text-sm">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-white font-medium">{trade.base_symbol}</p>
                                  {isFullyImported && (
                                    <span className="px-1.5 py-0.5 bg-slate-500/20 text-slate-400 text-[10px] font-medium rounded">
                                      IMPORTED
                                    </span>
                                  )}
                                  {canCloseExisting && (
                                    <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-[10px] font-medium rounded">
                                      CAN CLOSE
                                    </span>
                                  )}
                                  {canImportNew && (
                                    <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] font-medium rounded">
                                      NEW
                                    </span>
                                  )}
                                </div>
                                <p className="text-slate-600 text-xs">{trade.tradingsymbol}</p>
                              </div>
                              
                              <div className="text-center">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  trade.direction === "LONG" ? 'bg-profit/20 text-profit' : 'bg-loss/20 text-loss'
                                }`}>
                                  {trade.direction}
                                </span>
                                <p className={`text-xs mt-1 ${trade.status === "CLOSED" ? 'text-blue-400' : 'text-yellow-400'}`}>
                                  {trade.status}
                                </p>
                              </div>
                              
                              <div className="text-right">
                                <p className="text-white font-mono">₹{trade.entry_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                                <p className="text-slate-500 text-xs">{trade.entry_time}</p>
                              </div>
                              
                              <div className="text-right">
                                {trade.exit_price ? (
                                  <>
                                    <p className="text-white font-mono">₹{trade.exit_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                                    <p className="text-slate-500 text-xs">{trade.exit_time}</p>
                                  </>
                                ) : (
                                  <p className="text-slate-500 text-xs">Open</p>
                                )}
                              </div>
                              
                              <div className="text-right">
                                <p className="text-white font-mono">{trade.entry_quantity}</p>
                                <p className="text-slate-500 text-xs">{trade.num_lots} lots</p>
                              </div>
                              
                              <div className="text-right">
                                {trade.pnl_amount !== undefined ? (
                                  <p className={`font-mono ${trade.pnl_amount >= 0 ? 'text-profit' : 'text-loss'}`}>
                                    ₹{trade.pnl_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                  </p>
                                ) : (
                                  <p className="text-slate-500 text-xs">-</p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Comments input for CAN CLOSE trades */}
                          {canCloseExisting && selectedTradeBookTrades.includes(key) && (
                            <div className="mt-2 pt-2 border-t border-orange-500/20">
                              <input
                                type="text"
                                placeholder="Exit comments (optional)"
                                value={tradeBookComments[idx] || ""}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setTradeBookComments(prev => ({
                                    ...prev,
                                    [idx]: e.target.value
                                  }));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full px-3 py-2 bg-black/30 border border-orange-500/30 rounded text-sm text-white placeholder-slate-500 focus:outline-none focus:border-orange-500/50"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                    </>
                  )}
                </div>
              )
            )}
          </div>

          <DialogFooter className="gap-2 pt-4 border-t border-white/10">
            <Button variant="outline" onClick={() => setShowImportModal(false)} className="border-white/10 text-slate-300">
              Cancel
            </Button>
            {importMode === "positions" ? (
              <Button 
                onClick={handleImportPositions} 
                disabled={importingPositions || selectedPositions.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {importingPositions ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Import {selectedPositions.length} Position{selectedPositions.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={handleImportFromTradeBook} 
                disabled={importingPositions || selectedTradeBookTrades.length === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {importingPositions ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Process {selectedTradeBookTrades.length} Trade{selectedTradeBookTrades.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reconcile Modal */}
      {/* Live Chart Popup */}
      <TradeChartModal open={!!chartConfig} onClose={() => setChartConfig(null)} config={chartConfig} />

      <ReconcileModal
        isOpen={showReconcileModal}
        onClose={setShowReconcileModal}
        reconcileData={reconcileData}
        loading={loadingReconcile}
        exitPrice={reconcileExitPrice}
        setExitPrice={setReconcileExitPrice}
        exitDate={reconcileExitDate}
        setExitDate={setReconcileExitDate}
        exitComments={reconcileExitComments}
        setExitComments={setReconcileExitComments}
        onCloseFromReconcile={handleCloseFromReconcile}
        isClosing={closingFromReconcile}
      />
      {/* Historical Import Modal */}
      <HistoricalImportModal
        isOpen={showHistoricalImportModal}
        onClose={setShowHistoricalImportModal}
        accounts={accounts}
        selectedAccountId={historicalImportAccountId}
        setSelectedAccountId={setHistoricalImportAccountId}
        selectedFile={historicalImportFile}
        setSelectedFile={setHistoricalImportFile}
        onImport={handleHistoricalImport}
        isImporting={importingHistorical}
        onViewHistory={openImportBatchesModal}
      />

      {/* Import Batches Management Modal */}
      <Dialog open={showImportBatchesModal} onOpenChange={setShowImportBatchesModal}>
        <DialogContent className="bg-charcoal border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-400" />
              Historical Import History
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View past imports, check details, and rollback if needed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingBatches ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-400">Loading import history...</span>
              </div>
            ) : importBatches.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No historical imports found</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {importBatches.map((batch) => (
                  <div 
                    key={batch.batch_id} 
                    className="bg-black/20 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-white font-medium">{batch.account_name}</span>
                          {batch.filename && (
                            <span className="text-xs text-slate-500">{batch.filename}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">
                            {batch.trade_count} imported
                          </span>
                          {batch.skipped_count > 0 && (
                            <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded">
                              {batch.skipped_count} skipped
                            </span>
                          )}
                          {batch.error_count > 0 && (
                            <span className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded">
                              {batch.error_count} errors
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {batch.import_date ? new Date(batch.import_date).toLocaleString() : batch.date_range}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchImportLogDetail(batch.batch_id)}
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
                          data-testid={`view-log-btn-${batch.batch_id}`}
                        >
                          <FileSpreadsheet className="w-3 h-3 mr-1" />
                          View Log
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRollbackImport(batch.batch_id)}
                          disabled={rollingBackBatch === batch.batch_id}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid={`rollback-btn-${batch.batch_id}`}
                        >
                          {rollingBackBatch === batch.batch_id ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              Rolling back...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3 h-3 mr-1" />
                              Rollback
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowImportBatchesModal(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Log Detail Modal */}
      <Dialog open={showImportLogDetail} onOpenChange={setShowImportLogDetail}>
        <DialogContent className="bg-charcoal border-white/10 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-400" />
              Import Log Details
            </DialogTitle>
            {selectedImportLog && (
              <DialogDescription className="text-slate-400">
                {selectedImportLog.account_name} - {selectedImportLog.filename || selectedImportLog.batch_id}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingImportLog ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-400">Loading log details...</span>
              </div>
            ) : selectedImportLog ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-black/20 border border-white/10 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-white mb-2">Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400">Total Parsed</p>
                      <p className="text-white font-medium">{selectedImportLog.total_parsed || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Imported</p>
                      <p className="text-green-400 font-medium">{selectedImportLog.imported_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Skipped (Duplicates)</p>
                      <p className="text-yellow-400 font-medium">{selectedImportLog.skipped_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Errors</p>
                      <p className="text-red-400 font-medium">{selectedImportLog.error_count || 0}</p>
                    </div>
                  </div>
                  {selectedImportLog.message && (
                    <p className="mt-2 text-xs text-slate-400">{selectedImportLog.message}</p>
                  )}
                </div>

                {/* Errors Section */}
                {selectedImportLog.errors && selectedImportLog.errors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-400 mb-2">
                      Errors ({selectedImportLog.errors.length})
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedImportLog.errors.map((err, idx) => (
                        <div key={`${err.symbol || "err"}-${err.entry_date || idx}-${idx}`} className="text-xs bg-black/20 rounded p-2">
                          <span className="text-white">{err.symbol}</span>
                          {err.entry_date && <span className="text-slate-400 ml-2">{err.entry_date}</span>}
                          {err.entry_price && <span className="text-slate-400 ml-2">@ ₹{err.entry_price}</span>}
                          <p className="text-red-300 mt-1">{err.error}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skipped Duplicates Section */}
                {selectedImportLog.skipped_duplicates && selectedImportLog.skipped_duplicates.length > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-yellow-400 mb-2">
                      Skipped Duplicates ({selectedImportLog.skipped_duplicates.length})
                    </h4>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {selectedImportLog.skipped_duplicates.map((dup, idx) => (
                        <div key={`${dup.symbol}-${dup.direction}-${dup.entry_date}-${idx}`} className="text-xs flex items-center gap-2 py-1 border-b border-white/5 last:border-0">
                          <span className="text-white font-medium">{dup.symbol}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${dup.direction === 'LONG' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                            {dup.direction}
                          </span>
                          <span className="text-slate-400">{dup.entry_date}</span>
                          <span className="text-slate-300">@ ₹{dup.entry_price}</span>
                          {dup.reason && <span className="text-slate-500 text-[10px]">({dup.reason})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Imported Trades Section */}
                {selectedImportLog.imported_trades && selectedImportLog.imported_trades.length > 0 && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-green-400 mb-2">
                      Imported Trades ({selectedImportLog.imported_trades.length})
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-slate-400 border-b border-white/10">
                            <th className="text-left py-2 pr-2">Symbol</th>
                            <th className="text-left py-2 pr-2">Dir</th>
                            <th className="text-left py-2 pr-2">Entry</th>
                            <th className="text-right py-2 pr-2">Entry ₹</th>
                            <th className="text-left py-2 pr-2">Exit</th>
                            <th className="text-right py-2 pr-2">Exit ₹</th>
                            <th className="text-right py-2">P&L</th>
                          </tr>
                        </thead>
                        <tbody className="max-h-60 overflow-y-auto">
                          {selectedImportLog.imported_trades.slice(0, 100).map((trade, idx) => (
                            <tr key={trade.id || `${trade.symbol}-${trade.entry_date}-${idx}`} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-1.5 pr-2 text-white">{trade.symbol}</td>
                              <td className="py-1.5 pr-2">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${trade.direction === 'LONG' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                                  {trade.direction}
                                </span>
                              </td>
                              <td className="py-1.5 pr-2 text-slate-400">{trade.entry_date}</td>
                              <td className="py-1.5 pr-2 text-right text-slate-300">{trade.entry_price}</td>
                              <td className="py-1.5 pr-2 text-slate-400">{trade.exit_date}</td>
                              <td className="py-1.5 pr-2 text-right text-slate-300">{trade.exit_price || '-'}</td>
                              <td className={`py-1.5 text-right ${trade.pnl_amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {trade.pnl_amount ? `₹${trade.pnl_amount.toLocaleString()}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {selectedImportLog.imported_trades.length > 100 && (
                        <p className="text-xs text-slate-500 mt-2 text-center">
                          Showing first 100 of {selectedImportLog.imported_trades.length} trades
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <p>No log data available</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t border-white/10 pt-4">
            {selectedImportLog && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRollbackImport(selectedImportLog.batch_id)}
                disabled={rollingBackBatch === selectedImportLog.batch_id}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 mr-auto"
              >
                {rollingBackBatch === selectedImportLog.batch_id ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Rolling back...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3 h-3 mr-1" />
                    Rollback This Import
                  </>
                )}
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => {
                setShowImportLogDetail(false);
                setSelectedImportLog(null);
              }}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TradesList;
